import { FunctionComponent } from "react";

import { Radio } from "@/components/Radio/Radio";
import { IcpPosition } from "@/pages/Private/pages/List/pages/ListEditDetails/enum/icp.enum";

import {
	OnboardingIcpPosition,
	OnboardingListTypeKey,
	PeopleAtCompanyConfig,
} from "../onboardingListTypes";
import { ConfigFieldErrors } from "../onboardingListValidation";
import { ConfigField, ConfigGrid, useConfigTranslation, useFieldError } from "./ConfigField";
import { OptionMultiField } from "./FilterFields";
import { OnboardingFilterOptions } from "./useOnboardingFilterOptions";
import { PeoplePerCompanyStepper } from "./PeoplePerCompanyStepper";
import { SuggestingTextField } from "./SuggestingTextField";

const POSITION_OPTIONS: { position: OnboardingIcpPosition; labelKey: string }[] = [
	{ position: IcpPosition.CEO_AND_SIMILAR, labelKey: "positionCeo" },
	{ position: IcpPosition.CUSTOM, labelKey: "positionTitle" },
	{ position: IcpPosition.DEPARTMENT_SENIORITY, labelKey: "positionDepartment" },
];

/**
 * The minified "who to find at each qualifying company" profile of the in-app
 * details step (`DefineIcps`), shown for the list types whose source only finds
 * companies. Onboarding collects one search profile instead of three, and skips
 * the persona segment (no persona categories exist before checkout) and the
 * personal-location column (onboarding lists search anywhere).
 */
export const PeopleAtCompanyFields: FunctionComponent<{
	value: PeopleAtCompanyConfig;
	/** Keeps the radio group unique across the rows of the picker. */
	typeKey: OnboardingListTypeKey;
	errors: ConfigFieldErrors;
	options: OnboardingFilterOptions;
	onChange: (value: PeopleAtCompanyConfig) => void;
}> = ({ value, typeKey, errors, options, onChange }) => {
	const ts = useConfigTranslation();
	const errorFor = useFieldError(errors);
	const error = errorFor("peopleAtCompany");

	return (
		<div className="mt-1 rounded-lg border border-gray-200 bg-brand-white p-3">
			<div className="label-styles mb-0.5">{ts("peopleAtCompany")}</div>
			<div className="mb-3 text-xs text-text-meta">{ts("peopleAtCompanyHint")}</div>

			<div className="mb-3 flex flex-col gap-2">
				{POSITION_OPTIONS.map(({ position, labelKey }) => (
					<Radio
						key={position}
						isChecked={value.position === position}
						name={`peopleAtCompany-${typeKey}`}
						testId={`onboarding-${typeKey}-position-${position}`}
						value={position}
						onChange={() => onChange({ ...value, position })}
					>
						<span className="ml-2 text-ssm text-text-meta">{ts(labelKey)}</span>
					</Radio>
				))}
			</div>

			{value.position === IcpPosition.CUSTOM && (
				<ConfigField required error={error} label={ts("jobTitle")}>
					<SuggestingTextField
						error={Boolean(error)}
						fetchFn={options.fetchJobTitles}
						placeholder={ts("jobTitlePlaceholder")}
						value={value.title}
						onChange={(title) => onChange({ ...value, title })}
					/>
				</ConfigField>
			)}

			{value.position === IcpPosition.DEPARTMENT_SENIORITY && (
				<ConfigGrid>
					<OptionMultiField
						error={error}
						label={ts("seniority")}
						options={options.seniorityOptions}
						placeholder={ts("seniorityPlaceholder")}
						values={value.seniority}
						onChange={(seniority) => onChange({ ...value, seniority })}
					/>
					<OptionMultiField
						label={ts("department")}
						options={options.departmentOptions}
						placeholder={ts("departmentPlaceholder")}
						values={value.department}
						onChange={(department) => onChange({ ...value, department })}
					/>
				</ConfigGrid>
			)}

			<PeoplePerCompanyStepper
				value={value.maxPerCompany}
				onChange={(maxPerCompany) => onChange({ ...value, maxPerCompany })}
			/>
		</div>
	);
};
